import { NextPageWithLayout } from "@bptypes/layout";
import { SpriteIcon } from "@components/core/icons/SpriteIcon";
import { TitleControls } from "@components/headings";
import { TracksList } from "@components/lists/TracksList";
import { TracksTable } from "@components/tables/TracksTable";
import MainLayout from "@layouts/MainLayout";
import { DEFAULT_LOCALE } from "@lib/constants";
import { exportAnonSession, useSessionContext } from "@lib/context/session";
import { useMediaQuery } from "@lib/hooks/useMediaQuery";
import { getGenreQuery } from "@lib/network/genres";
import { getGenreTopTracksQuery } from "@lib/network/tracks";
import { getLocationData } from "@lib/utils/getLocationData";
import { device } from "@styles/theme";
import { dehydrate, DehydratedState, QueryClient, useQuery } from "@tanstack/react-query";
import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import { i18n } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { ReactElement } from "react";

const totalTracks = 100;

interface Props {
	anonSession: AnonSession;
}

const Top100: NextPageWithLayout<Props> = ({ anonSession }) => {
	const isDesktop = useMediaQuery({ query: device.xl });
	const { getAccessToken, importAnonSession } = useSessionContext();
	importAnonSession(anonSession);
	const accessToken = getAccessToken();
	const router = useRouter();
	const { id } = router.query;
	const genreId = id ? parseInt(id as string, 10) : 0;

	if (genreId === 0) {
		router.push("/");
	}

	const tracksQuery = useQuery(
		getGenreTopTracksQuery({
			params: {
				per_page: totalTracks,
				hype: true,
			},
			accessToken,
			genreId,
			number: totalTracks,
		}),
	);

	const { data: genre } = useQuery(getGenreQuery({ genreId, accessToken }));

	return (
		<>
			<TitleControls
				showPlay
				showAddToQueue
				showAddToPlaylist
				tracks={tracksQuery.data ? tracksQuery.data.results : []}
				preText={genre.name}
				title={<SpriteIcon id="hype-top-100" width="169" height="28" />}
			/>

			{isDesktop ?
					(
						<TracksTable
							location={`Genre Page - ${genre.name} - Hype Top 100`}
							isLoading={tracksQuery.isLoading}
							tracks={tracksQuery.data ? tracksQuery.data.results : []}
						/>
					) :
					(
						<TracksList
							location={`Genre Page - ${genre.name} - Hype Top 100`}
							isLoading={tracksQuery.isLoading}
							tracks={tracksQuery.data ? tracksQuery.data.results : []}
							showArtwork
							showNumbers
						/>
					)}
		</>
	);
};

export default Top100;

Top100.getLayout = (page: ReactElement) => {
	return (
		<MainLayout
			title={`Beatport Hype ${i18n?.t("Top.Top100Songs")}`}
			metaDescription={i18n?.t("Top.CheckOutTop100")}
		>
			{page}
		</MainLayout>
	);
};

type ServerSideProps = {
	dehydratedState: DehydratedState;
	anonSession: AnonSession;
};

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async (
	ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<ServerSideProps>> => {
	const queryClient = new QueryClient();
	const { id } = ctx.params as { id: string };
	const genreId = id ? parseInt(id as string, 10) : 0;
	const location = getLocationData(ctx.req);

	if (genreId > 0) {
		await Promise.all([
			queryClient
				.fetchQuery(
					getGenreTopTracksQuery({
						params: {
							per_page: totalTracks,
							hype: true,
						},
						genreId,
						number: totalTracks,
						location,
					}),
				)
				.then((res) => res)
				.catch(() => { }),
			queryClient
				.fetchQuery(getGenreQuery({ genreId }))
				.then((res) => res)
				.catch(() => { }),
		]);
	}

	const anonSession = await exportAnonSession();

	return {
		props: {
			dehydratedState: dehydrate(queryClient),
			anonSession,
			...(await serverSideTranslations(ctx.locale ?? DEFAULT_LOCALE, ["translation"])),
		},
	};
};
